import { Controller } from "@hotwired/stimulus"
import Swal from 'sweetalert2'

export default class extends Controller {
  static values = {
    title: String,
    text: String,
    icon: String,
    confirmButtonText: String,
    cancelButtonText: String,
    confirmButtonColor: String,
    cancelButtonColor: String,
    showCancelButton: { type: Boolean, default: true },
    reverseButtons: { type: Boolean, default: false },
    focusCancel: { type: Boolean, default: false },
    html: String,
    imageUrl: String,
    imageWidth: Number,
    imageHeight: Number,
    imageAlt: String,
    timer: Number,
    timerProgressBar: { type: Boolean, default: false },
    allowOutsideClick: { type: Boolean, default: true },
    allowEscapeKey: { type: Boolean, default: true },
    allowEnterKey: { type: Boolean, default: true },
    showClass: Object,
    hideClass: Object,
    customClass: Object,
    position: String,
    backdrop: { type: Boolean, default: true },
    toast: { type: Boolean, default: false },
    target: String,
    grow: String,
    width: String,
    padding: String,
    background: String,
    input: String,
    inputLabel: String,
    inputPlaceholder: String,
    inputValue: String,
    inputOptions: Object,
    inputAutoTrim: { type: Boolean, default: true },
    inputAttributes: Object,
    inputValidator: String,
    validationMessage: String,
    preConfirm: String,
    preDeny: String,
    showDenyButton: { type: Boolean, default: false },
    denyButtonText: String,
    denyButtonColor: String,
    showLoaderOnConfirm: { type: Boolean, default: false },
    showLoaderOnDeny: { type: Boolean, default: false }
  }

  connect() {
    this.element.addEventListener('click', this.handleClick.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('click', this.handleClick.bind(this))
  }

  handleClick(event) {
    if (this.element.dataset.sweetalertBypassed === 'true') {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const options = this.buildOptions()

    Swal.fire(options).then((result) => {
      if (result.isConfirmed) {
        this.executeOriginalAction()
      } else if (result.isDenied && this.showDenyButtonValue) {
        this.handleDeny()
      }
    })
  }

  buildOptions() {
    const options = {}

    if (this.hasTitleValue) options.title = this.titleValue
    if (this.hasTextValue) options.text = this.textValue
    if (this.hasHtmlValue) options.html = this.htmlValue
    if (this.hasIconValue) options.icon = this.iconValue

    options.showCancelButton = this.showCancelButtonValue
    if (this.hasConfirmButtonTextValue) options.confirmButtonText = this.confirmButtonTextValue
    if (this.hasCancelButtonTextValue) options.cancelButtonText = this.cancelButtonTextValue
    if (this.hasConfirmButtonColorValue) options.confirmButtonColor = this.confirmButtonColorValue
    if (this.hasCancelButtonColorValue) options.cancelButtonColor = this.cancelButtonColorValue
    options.reverseButtons = this.reverseButtonsValue
    options.focusCancel = this.focusCancelValue

    if (this.showDenyButtonValue) {
      options.showDenyButton = true
      if (this.hasDenyButtonTextValue) options.denyButtonText = this.denyButtonTextValue
      if (this.hasDenyButtonColorValue) options.denyButtonColor = this.denyButtonColorValue
    }

    if (this.hasImageUrlValue) {
      options.imageUrl = this.imageUrlValue
      if (this.hasImageWidthValue) options.imageWidth = this.imageWidthValue
      if (this.hasImageHeightValue) options.imageHeight = this.imageHeightValue
      if (this.hasImageAltValue) options.imageAlt = this.imageAltValue
    }

    if (this.hasTimerValue) {
      options.timer = this.timerValue
      options.timerProgressBar = this.timerProgressBarValue
    }

    options.allowOutsideClick = this.allowOutsideClickValue
    options.allowEscapeKey = this.allowEscapeKeyValue
    options.allowEnterKey = this.allowEnterKeyValue

    if (this.hasPositionValue) options.position = this.positionValue
    options.backdrop = this.backdropValue
    options.toast = this.toastValue
    if (this.hasTargetValue) options.target = this.targetValue
    if (this.hasGrowValue) options.grow = this.growValue
    if (this.hasWidthValue) options.width = this.widthValue
    if (this.hasPaddingValue) options.padding = this.paddingValue
    if (this.hasBackgroundValue) options.background = this.backgroundValue

    if (this.hasShowClassValue) options.showClass = this.showClassValue
    if (this.hasHideClassValue) options.hideClass = this.hideClassValue
    if (this.hasCustomClassValue) options.customClass = this.customClassValue

    if (this.hasInputValue) {
      options.input = this.inputValue
      if (this.hasInputLabelValue) options.inputLabel = this.inputLabelValue
      if (this.hasInputPlaceholderValue) options.inputPlaceholder = this.inputPlaceholderValue
      if (this.hasInputValueValue) options.inputValue = this.inputValueValue
      if (this.hasInputOptionsValue) options.inputOptions = this.inputOptionsValue
      options.inputAutoTrim = this.inputAutoTrimValue
      if (this.hasInputAttributesValue) options.inputAttributes = this.inputAttributesValue
      if (this.hasValidationMessageValue) options.validationMessage = this.validationMessageValue
    }

    options.showLoaderOnConfirm = this.showLoaderOnConfirmValue
    options.showLoaderOnDeny = this.showLoaderOnDenyValue

    return options
  }

  executeOriginalAction() {
    const element = this.element

    this.element.removeEventListener('click', this.handleClick.bind(this))

    element.dataset.sweetalertBypassed = 'true'

    if (element.tagName === 'A') {
      const href = element.getAttribute('href')
      const method = element.dataset.turboMethod || element.dataset.method || 'get'

      if (method.toLowerCase() === 'get') {
        Turbo.visit(href)
      } else {
        const form = document.createElement('form')
        form.method = 'post'
        form.action = href

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        if (csrfToken) {
          const csrfInput = document.createElement('input')
          csrfInput.type = 'hidden'
          csrfInput.name = 'authenticity_token'
          csrfInput.value = csrfToken
          form.appendChild(csrfInput)
        }

        const methodInput = document.createElement('input')
        methodInput.type = 'hidden'
        methodInput.name = '_method'
        methodInput.value = method
        form.appendChild(methodInput)

        document.body.appendChild(form)
        form.requestSubmit()
        document.body.removeChild(form)
      }
    } else if (element.tagName === 'BUTTON' || element.tagName === 'INPUT') {
      const form = element.closest('form')
      if (form) {
        if (element.type === 'submit') {
          form.requestSubmit(element)
        } else {
          element.click()
        }
      } else {
        element.click()
      }
    }

    setTimeout(() => {
      delete element.dataset.sweetalertBypassed
      this.element.addEventListener('click', this.handleClick.bind(this))
    }, 100)
  }

  handleDeny() {
    console.log('Action denied')
  }
}